import React, { useMemo } from 'react';
import { View, Text, Image, TextInput, StyleSheet } from 'react-native';
import { parseScheduleText } from './ScheduleEditor';
import { formatScheduleTimeDisplay } from '../utils/scheduleTimeUtils';
import { useMasterTourCalendarUi } from '../contexts/FeatureFlagsContext';
import {
  DAY_SHEET_PAGE_MM,
  daySheetTypeScale,
  mmToPt,
} from '../utils/daySheetFit';

const PAPER_W = DAY_SHEET_PAGE_MM.width;
const PAPER_H = DAY_SHEET_PAGE_MM.height;
const INNER_PAD_X = 34;
const INNER_PAD_TOP = 18;
const INNER_PAD_BOTTOM = 18;
const BANNER_RATIO_INNER = 0.25;
const INFO_MARGIN_X = 30;
const INFO_MARGIN_BOTTOM = 50;
const SCHEDULE_PAD_X = 30;
const ROW_STEP = 5.2;
const HEADER_BOX_H = 14;

function fallbackRows() {
  return [
    'LOBBY CALL',
    'VENUE ACCESS',
    'LOAD IN SUPPORT',
    'LOAD IN LINECHECK',
    'ARTIST 1 SOUNDCHECK',
    'ARTIST 2 SOUNDCHECK',
    'ARTIST 3 SOUNDCHECK',
    'DOORS',
    'ARTIST 3',
    'ARTIST 2',
    'ARTIST 1',
    'VENUE CURFEW',
    'BUS CALL',
  ].map((item, idx) => ({ id: `default-${idx}`, item, time: '0:00' }));
}

/**
 * @param {string} scheduleRaw
 * @param {{ useDefaults?: boolean }} [options] useDefaults true (default): when parse yields no rows, return template rows for legacy PDFs.
 */
export function buildDaySheetPreviewRows(scheduleRaw, options = {}) {
  const useDefaults = options.useDefaults !== false;
  const rows = parseScheduleText(String(scheduleRaw || '')).lines || [];
  const mapped = rows
    .map((ln) => {
      if (ln.kind === 'schedule') {
        const time = formatScheduleTimeDisplay(ln.time, ln.period || 'AM') || String(ln.time || '').trim();
        return {
          id: ln.id,
          item: String(ln.item || '').trim() || 'ITEM',
          time: time || '0:00',
        };
      }
      if (ln.kind === 'text') {
        return {
          id: ln.id,
          item: String(ln.text || '').trim(),
          time: '',
        };
      }
      return null;
    })
    .filter(Boolean);
  if (mapped.length) return mapped;
  return useDefaults ? fallbackRows() : [];
}

export async function downloadDaySheetPdf({
  tourName,
  dateKey,
  headline,
  venueName,
  cityLine,
  bannerUrl,
  backgroundUrl,
  scheduleRows,
  aftershowText,
  fileName,
  openWindow = null,
}) {
  const mod = await import('jspdf');
  const JsPdf = mod?.jsPDF;
  if (!JsPdf) return;
  const rows = Array.isArray(scheduleRows) ? scheduleRows : fallbackRows();
  const pdf = new JsPdf({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageW = PAPER_W;
  const pageH = PAPER_H;
  const hasBanner = Boolean(String(bannerUrl || '').trim());
  const aftershow = String(aftershowText || '').trim();
  const scale = daySheetTypeScale({
    pageHeight: pageH,
    rowCount: Math.max(1, rows.length),
    hasBanner,
    hasAftershow: Boolean(aftershow),
  });

  pdf.setDrawColor(20, 20, 26);
  pdf.setTextColor(20, 20, 26);
  const toDataUrl = async (url) => {
    const src = String(url || '').trim();
    if (!src) return null;
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return null;
    }
  };
  const mimeFromDataUrl = (dataUrl) => {
    const m = String(dataUrl || '').match(/^data:image\/([a-zA-Z0-9+]+);/);
    if (!m) return 'JPEG';
    const t = String(m[1] || '').toUpperCase();
    if (t.includes('PNG')) return 'PNG';
    if (t.includes('WEBP')) return 'WEBP';
    return 'JPEG';
  };
  const drawContainImage = (dataUrl, fmt, x, yPos, w, h) => {
    try {
      const props = pdf.getImageProperties(dataUrl);
      const iw = Number(props?.width || 0);
      const ih = Number(props?.height || 0);
      if (!iw || !ih) return false;
      const fit = Math.min(w / iw, h / ih);
      const dw = iw * fit;
      const dh = ih * fit;
      const dx = x + (w - dw) / 2;
      const dy = yPos + (h - dh) / 2;
      pdf.addImage(dataUrl, fmt, dx, dy, dw, dh, undefined, 'FAST');
      return true;
    } catch {
      return false;
    }
  };

  const bgData = await toDataUrl(backgroundUrl);
  if (bgData) {
    drawContainImage(bgData, mimeFromDataUrl(bgData), 0, 0, pageW, pageH);
  }

  const side = 18;
  let y = scale.padTop;
  const bannerData = hasBanner ? await toDataUrl(bannerUrl) : null;
  if (hasBanner && bannerData) {
    drawContainImage(bannerData, mimeFromDataUrl(bannerData), side, y, pageW - side * 2, scale.bannerH);
    y += scale.bannerH + 2;
  } else if (hasBanner) {
    y += scale.bannerH;
  }

  const title = String(tourName || 'Day sheet').trim();
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(mmToPt(scale.titleSize));
  pdf.setTextColor(20, 20, 26);
  pdf.text(title.toUpperCase(), pageW / 2, y + scale.titleH * 0.72, { align: 'center' });
  y += scale.titleH;

  const boxTop = y;
  const boxH = scale.infoH;
  pdf.setLineWidth(0.45);
  pdf.rect(side + 8, boxTop, pageW - (side + 8) * 2, boxH);
  pdf.setTextColor(200, 16, 46);
  try {
    pdf.setFont('helvetica', 'bolditalic');
  } catch {
    pdf.setFont('helvetica', 'italic');
  }
  pdf.setFontSize(mmToPt(scale.infoSize));
  const headerLines = [
    String(headline || dateKey || '').trim(),
    String(venueName || '').trim(),
    String(cityLine || '').trim(),
  ].filter(Boolean);
  const lineGap = scale.infoSize * 1.15;
  const textBlock = headerLines.length * lineGap;
  let hy = boxTop + (boxH - textBlock) / 2 + scale.infoSize * 0.85;
  headerLines.forEach((line) => {
    pdf.text(line, pageW / 2, hy, { align: 'center' });
    hy += lineGap;
  });
  y = boxTop + boxH + 4;

  pdf.setTextColor(20, 20, 26);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(mmToPt(scale.cueSize));
  const schedLeft = side + 10;
  const timeX = pageW - side - 10;
  rows.forEach((row) => {
    const item = String(row?.item || '').trim();
    const rawTime = String(row?.time || '').trim();
    if (!item && !rawTime) {
      y += scale.rowH;
      return;
    }
    const baseline = y + scale.rowH * 0.72;
    if (item) pdf.text(item.toUpperCase(), schedLeft, baseline);
    if (rawTime) pdf.text(rawTime, timeX, baseline, { align: 'right' });
    y += scale.rowH;
  });

  if (aftershow) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(mmToPt(Math.max(scale.afterSize, 3.2)));
    const lines = pdf.splitTextToSize(aftershow, pageW - side * 2);
    const maxLines = Math.max(1, Math.floor(scale.afterH / Math.max(scale.afterSize * 1.2, 3)));
    pdf.text(lines.slice(0, maxLines), pageW / 2, y + scale.afterSize, { align: 'center' });
  }

  const name = fileName || `day-sheet_${dateKey || 'date'}.pdf`;
  if (openWindow && typeof openWindow.location !== 'undefined') {
    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    openWindow.location = url;
    return;
  }
  pdf.save(name);
}

export function DaySheetPaper({
  theme,
  tourName,
  headline,
  venueName,
  cityLine,
  bannerUrl,
  backgroundUrl,
  scheduleRows,
  aftershowText,
  renderSchedule,
  renderAftershow,
  exportNodeId,
  editMode = false,
  compact = false,
  aftershowPlaceholder = 'AFTERSHOW...',
  onAftershowChange,
}) {
  const v2Layout = useMasterTourCalendarUi();
  const styles = useMemo(() => createStyles(editMode, compact, v2Layout), [editMode, compact, v2Layout]);
  const rows = Array.isArray(scheduleRows) && scheduleRows.length > 0 ? scheduleRows : [];
  const customSchedule = typeof renderSchedule === 'function' ? renderSchedule() : null;
  const customAftershow = typeof renderAftershow === 'function' ? renderAftershow() : null;
  const hasBanner = Boolean(String(bannerUrl || '').trim());

  return (
    <View style={styles.paper} nativeID={exportNodeId}>
      {backgroundUrl ? (
        <Image source={{ uri: backgroundUrl }} style={styles.bgImage} resizeMode="cover" />
      ) : null}
      <View style={styles.inner}>
        {v2Layout && String(tourName || '').trim() ? (
          <Text style={styles.tourName} numberOfLines={2}>
            {String(tourName).trim()}
          </Text>
        ) : null}
        {hasBanner ? (
          <Image source={{ uri: bannerUrl }} style={styles.bannerImage} resizeMode="contain" />
        ) : null}
        <View style={styles.infoBox}>
          <Text style={styles.infoText} numberOfLines={2}>
            {headline || ''}
          </Text>
          {venueName ? (
            <Text style={styles.infoText} numberOfLines={2}>
              {venueName}
            </Text>
          ) : null}
          {cityLine ? (
            <Text style={styles.infoText} numberOfLines={2}>
              {cityLine}
            </Text>
          ) : null}
        </View>
        <View style={styles.scheduleBlock}>
          {customSchedule
            ? customSchedule
            : rows.map((row) => (
                <View key={`sheet-row-${row.id}`} style={styles.previewRow}>
                  <Text style={styles.previewItem} numberOfLines={2}>
                    {String(row.item || '').trim() || ' '}
                  </Text>
                  <Text style={styles.previewTime}>{String(row.time || '').trim()}</Text>
                </View>
              ))}
        </View>
        {customAftershow ? (
          customAftershow
        ) : editMode && onAftershowChange ? (
          <TextInput
            style={styles.aftershowInput}
            value={String(aftershowText || '')}
            onChangeText={onAftershowChange}
            placeholder={aftershowPlaceholder}
            placeholderTextColor="#888888"
            multiline
            textAlign="center"
          />
        ) : (
          <Text style={styles.aftershowPreview}>{aftershowText || aftershowPlaceholder}</Text>
        )}
      </View>
    </View>
  );
}

function createStyles(editMode, compact, v2Layout = false) {
  const padX = compact ? 16 : INNER_PAD_X;
  const padTop = compact ? 10 : INNER_PAD_TOP;
  const padBottom = compact ? 12 : INNER_PAD_BOTTOM;
  const infoMarginX = compact ? 12 : INFO_MARGIN_X;
  const infoMarginBottom = compact ? 12 : INFO_MARGIN_BOTTOM;
  const schedulePadX = compact ? 8 : SCHEDULE_PAD_X;
  const infoFont = compact ? 11 : 16;
  const rowFont = compact ? 11 : 14;
  const aftershowFont = compact ? 12 : 14;
  const bannerHeight = compact ? 56 : 100;

  return StyleSheet.create({
    paper: {
      width: '100%',
      maxWidth: compact ? undefined : (v2Layout ? 620 : 860),
      backgroundColor: v2Layout ? '#fffffe' : '#ffffff',
      borderWidth: 1,
      borderColor: v2Layout ? '#e4e4df' : 'rgba(0,0,0,0.15)',
      borderRadius: v2Layout ? 4 : 0,
      position: 'relative',
      ...(editMode
        ? {
            minHeight: compact ? 280 : 420,
            overflow: 'visible',
          }
        : {
            aspectRatio: 210 / 297,
            maxHeight: compact ? 520 : undefined,
            overflow: 'hidden',
          }),
    },
    bgImage: {
      ...StyleSheet.absoluteFillObject,
      width: '100%',
      height: '100%',
      opacity: compact ? 0.14 : 0.2,
    },
    inner: {
      flex: editMode ? undefined : 1,
      paddingHorizontal: v2Layout ? (compact ? 16 : 46) : padX,
      paddingTop: v2Layout ? (compact ? 16 : 40) : padTop,
      paddingBottom: v2Layout ? (compact ? 16 : 34) : padBottom,
      zIndex: 1,
    },
    tourName: {
      fontSize: compact ? 22 : 42,
      fontWeight: '700',
      letterSpacing: 0.4,
      lineHeight: compact ? 24 : 42,
      textAlign: 'center',
      textTransform: 'uppercase',
      color: '#14141a',
      marginBottom: compact ? 10 : 18,
      fontFamily: "'Public Sans', system-ui, sans-serif",
    },
    bannerImage: {
      width: '100%',
      height: bannerHeight,
      marginBottom: compact ? 6 : 10,
      alignSelf: 'center',
    },
    infoBox: {
      borderWidth: v2Layout ? 1.5 : 1,
      borderColor: v2Layout ? '#14141a' : '#000000',
      paddingVertical: v2Layout ? (compact ? 8 : 14) : (compact ? 5 : 8),
      paddingHorizontal: v2Layout ? (compact ? 10 : 18) : (compact ? 6 : 10),
      marginBottom: v2Layout ? (compact ? 14 : 34) : infoMarginBottom,
      marginHorizontal: v2Layout ? 0 : infoMarginX,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: v2Layout ? '#fffffe' : 'rgba(255,255,255,0.92)',
    },
    infoText: {
      color: v2Layout ? '#c8102e' : '#a40000',
      fontSize: v2Layout ? (compact ? 12 : 19) : infoFont,
      fontWeight: v2Layout ? '700' : '800',
      fontStyle: v2Layout ? 'italic' : 'normal',
      textAlign: 'center',
      textTransform: v2Layout ? 'none' : 'uppercase',
      lineHeight: v2Layout ? (compact ? 16 : 27) : (compact ? 14 : 20),
      fontFamily: v2Layout ? "Georgia, 'Times New Roman', serif" : undefined,
    },
    scheduleBlock: {
      ...(editMode || compact ? {} : { flex: 1 }),
      backgroundColor: 'transparent',
      paddingHorizontal: schedulePadX,
    },
    previewRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      gap: 4,
      marginBottom: compact ? 2 : 3,
      minHeight: compact ? 18 : 22,
    },
    previewItem: {
      flex: 1,
      minWidth: 0,
      color: '#000000',
      fontSize: rowFont,
      fontWeight: '700',
      textTransform: 'uppercase',
      lineHeight: compact ? 14 : 18,
    },
    previewTime: {
      minWidth: compact ? 48 : 56,
      flexShrink: 0,
      color: '#000000',
      fontSize: rowFont,
      fontWeight: '700',
      textAlign: 'right',
      lineHeight: compact ? 14 : 18,
    },
    aftershowPreview: {
      marginTop: compact ? 6 : 10,
      color: '#000000',
      fontSize: aftershowFont,
      fontWeight: '800',
      textAlign: 'center',
      backgroundColor: 'transparent',
      paddingHorizontal: 4,
      paddingVertical: 4,
    },
    aftershowInput: {
      marginTop: compact ? 6 : 10,
      color: '#000000',
      fontSize: aftershowFont,
      fontWeight: '800',
      textAlign: 'center',
      backgroundColor: 'rgba(255,255,255,0.85)',
      borderWidth: 0,
      borderBottomWidth: 1,
      borderBottomColor: 'rgba(0,0,0,0.2)',
      paddingHorizontal: 8,
      paddingVertical: 6,
      minHeight: compact ? 36 : 44,
      width: '100%',
    },
  });
}
